import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {UnlockedEvent$key} from './__generated__/UnlockedEvent.graphql'
import {EventViewerPermission} from './LockedEvent'
import type {LockedEventViewer$key} from './__generated__/LockedEventViewer.graphql'
import {TimelineRow} from './row/TimelineRow'
import {UnlockIcon} from '@primer/octicons-react'

type UnlockedEventProps = {
  queryRef: UnlockedEvent$key
  issueUrl: string
  issueRef?: LockedEventViewer$key
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export function UnlockedEvent({queryRef, issueUrl, issueRef, refAttribute}: UnlockedEventProps): JSX.Element {
  if (!issueRef) {
    return <></>
  }

  return (
    <UnlockedEventInternal queryRef={queryRef} issueUrl={issueUrl} issueRef={issueRef} refAttribute={refAttribute} />
  )
}

function UnlockedEventInternal({
  queryRef,
  issueUrl,
  issueRef,
  highlightedEventId,
  refAttribute,
}: Omit<UnlockedEventProps, 'issueRef'> & {issueRef: LockedEventViewer$key}): JSX.Element {
  const {actor, createdAt, databaseId} = useFragment(
    graphql`
      fragment UnlockedEvent on UnlockedEvent {
        databaseId
        createdAt
        actor {
          ...EventActor
        }
      }
    `,
    queryRef,
  )

  const viewerData = useFragment(EventViewerPermission, issueRef)
  const highlighted = String(databaseId) === highlightedEventId

  const actorToUse = viewerData.repository.viewerCanPush ? actor : viewerData.repository.owner

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actorToUse}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={UnlockIcon}
    >
      <TimelineRow.Main>{`${LABELS.timeline.unlockedConversation} `}</TimelineRow.Main>
    </TimelineRow>
  )
}

try{ UnlockedEvent.displayName ||= 'UnlockedEvent' } catch {}
try{ UnlockedEventInternal.displayName ||= 'UnlockedEventInternal' } catch {}